// TypeScript
import { useEffect, useState } from 'react'

import { StructureObjectDto as StructureObjectDtoOld } from '@/endpoints/models'
import { useAppStore } from '@/hooks'
import {
	useSelectedBranchNumericId,
	useSelectedSystem,
} from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useLazyGetNodeDetailUsingGetQuery } from '@/rtkApi'
import { StructureDetailDto, StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { NativeMap } from '@/utils'
import { logError } from '@/utils/errorHandling'

interface FindSystemNodeIdAsyncProps {
	getNodeDetails: (
		parentNodeId: number,
	) => Promise<StructureDetailDto | StructureDto | undefined>
	initialNode: StructureDto
	/**
	 * System id already known from the selected tree system. The walk stops as soon as it
	 * reaches this id (or its direct child) instead of continuing up to the company ROOT,
	 * which has no ACL and would otherwise be hit unnecessarily / fail for non-admins.
	 */
	knownSystemId?: number
	nodes: NativeMap<StructureDto>
}

/**
 * Asynchronously finds the system node ID starting from an initial node by traversing
 * the hierarchy of its parent nodes. A system node is identified either by its type
 * being `SYSTEM` or if it has no parent structure ID.
 *
 * The company ROOT node has no ACL, so non-admin users cannot fetch it. To avoid relying
 * on that fetch succeeding, the walk stops early when it reaches the already-known system
 * (from the selected tree) or when the cached/fetched parent turns out to be the ROOT.
 * If fetching a parent fails (e.g. 403/404 because the user lacks VIEW on it), the walk
 * returns the parent id that could not be fetched rather than the child's id, since the
 * child id is never a valid system id to use for subsequent system-scoped calls.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.initialNode - The initial node from which the search begins.
 * @param {Object} params.nodes - A cache object where the keys are node IDs and the values are node objects.
 * @param {Function} params.getNodeDetails - An asynchronous function to fetch details
 * of a node given its ID. It returns a promise resolving to the node object.
 * @param {number} [params.knownSystemId] - System id already known from the selected tree system.
 * @returns {Promise<number>} A promise resolving to the ID of the system node
 * if found, or `0` if no system node is found.
 */
export const findSystemNodeIdAsync = async ({
	initialNode,
	nodes,
	getNodeDetails,
	knownSystemId,
}: FindSystemNodeIdAsyncProps): Promise<number> => {
	if (!initialNode || !initialNode.id) return 0

	let current: StructureDto | StructureDetailDto | undefined = initialNode

	while (current) {
		// Already resolved to the known selected system: no need to keep walking up to ROOT.
		if (knownSystemId && current.id === knownSystemId) {
			return knownSystemId
		}

		// If the current node is a system node or has no parent, it's the system node.
		if (
			current.type === StructureObjectDtoOld.TypeEnum.SYSTEM ||
			!current.parentStructureId
		) {
			return current.id as number
		}

		// The current node's parent is the already-known system: stop without fetching.
		if (knownSystemId && current.parentStructureId === knownSystemId) {
			return knownSystemId
		}

		// Try cache first.
		const cachedParent: StructureDto | undefined =
			nodes[current.parentStructureId]
		if (cachedParent?.type === StructureObjectDtoOld.TypeEnum.ROOT) {
			// Parent is the company ROOT (no ACL): the current node is the system.
			return current.id as number
		}
		if (cachedParent) {
			current = cachedParent
			continue
		}

		// Fallback: fetch missing parent.
		const fetchedParent = await getNodeDetails(current.parentStructureId)
		if (fetchedParent?.type === StructureObjectDtoOld.TypeEnum.ROOT) {
			// Parent is the company ROOT (no ACL): the current node is the system.
			return current.id as number
		}
		if (!fetchedParent) {
			// Defensive: the parent could not be fetched (e.g. no VIEW permission). Return the
			// parent id itself rather than the child, since the child is never a valid system id.
			return current.parentStructureId
		}

		current = fetchedParent
	}

	return 0
}

/**
 * Resolves the `systemId` for a given node by performing an upward traversal of the node hierarchy.
 * During the resolution process, it utilizes a combination of cached data and triggered API calls
 * to fetch missing node details.
 *
 * The hook maintains state to track the `systemId` and whether the resolution process is in progress.
 *
 * @param {StructureDto} node - The node for which the `systemId` needs to be resolved.
 * @returns {Object} An object containing the following properties:
 * - `systemId` {number}: The resolved system identifier for the given node. Defaults to 0 if resolution fails or is incomplete.
 * - `resolvingSystemId` {boolean}: Indicates whether the resolution process is currently active.
 */
export const useResolveSystemId = (node: StructureDto) => {
	const cachedNodes = useAppStore(
		(state) => state.node.nodes,
	) as NativeMap<StructureDto>

	const { selectedSystemId } = useSelectedSystem()
	const parsedSelectedSystemId = Number(selectedSystemId)
	const knownSystemId =
		selectedSystemId &&
		!Number.isNaN(parsedSelectedSystemId) &&
		parsedSelectedSystemId !== 0
			? parsedSelectedSystemId
			: undefined

	// Lazy loader for any missing parent during resolution
	const [triggerGetNodeDetail] = useLazyGetNodeDetailUsingGetQuery()
	const branchId = useSelectedBranchNumericId()

	// Resolve systemId with fetch-aware upward walk
	const [systemId, setSystemId] = useState<number>(0)
	const [resolvingSystemId, setResolvingSystemId] = useState<boolean>(false)

	useEffect(() => {
		let cancelled = false

		if (!node?.id) {
			setSystemId(0)
			setResolvingSystemId(false)
			return
		}

		const resolve = async () => {
			setResolvingSystemId(true)

			// Loader used by findSystemNodeIdAsync when parent is missing in cache
			const getNodeDetails = async (parentNodeId: number) => {
				try {
					// 1) Try Redux cache first
					const cachedNode = cachedNodes?.[parentNodeId]
					if (cachedNode) return cachedNode

					// 2) Fetch via RTK Query lazy trigger (unwrap returns the data)
					const fetchedNode = await triggerGetNodeDetail({
						branchId: branchId ?? 0,
						structureId: parentNodeId,
					}).unwrap()

					return fetchedNode
				} catch (e) {
					// The company ROOT (and other ancestors the user lacks VIEW on) are expected
					// to 403/404 for non-admins. Swallow those; log anything unexpected.
					const status =
						e && typeof e === 'object' && 'status' in e
							? (e as { status?: unknown }).status
							: undefined
					if (status !== 403 && status !== 404) {
						logError(e, {
							operation: 'useResolveSystemId.getNodeDetails',
							details: { parentNodeId },
						})
					}
					return undefined
				}
			}

			const fetchedId = await findSystemNodeIdAsync({
				initialNode: node,
				nodes: cachedNodes,
				getNodeDetails,
				knownSystemId,
			})

			if (!cancelled) {
				setSystemId(fetchedId)
				setResolvingSystemId(false)
			}
		}

		resolve()

		// Proper cleanup function for useEffect
		return () => {
			cancelled = true
		}
	}, [node, node?.id, cachedNodes, triggerGetNodeDetail, knownSystemId, branchId])

	return { systemId, resolvingSystemId }
}
